import React, { useEffect, useState } from 'react';
import api from '../api';
import forumImg from '../assets/forum.jpg';

const CATEGORIES = [
  'UMA General',
  'Support',
  'Tips',
  'Feature Request',
  'AI',
  'Social',
];

const Forum = () => {
  const [threads, setThreads] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedThread, setSelectedThread] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newThread, setNewThread] = useState({ title: '', content: '', category: CATEGORIES[0] });
  const [reply, setReply] = useState('');
  const [creating, setCreating] = useState(false);
  const [posting, setPosting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [user, setUser] = useState({ id: null, username: '' });

  // Get user info from localStorage (assumes JWT payload is not available, so fetch from /api/users/me if needed)
  useEffect(() => {
    // Option 1: If you store user info in localStorage
    const username = localStorage.getItem('uma_username');
    const id = localStorage.getItem('uma_userid');
    if (username && id) {
      setUser({ id: Number(id), username });
    } else {
      // Option 2: Fetch from backend
      api.get('/users/me').then(res => {
        setUser({ id: res.data.id, username: res.data.username });
      }).catch(() => {});
    }
  }, []);

  // Fetch threads
  const fetchThreads = async (category = selectedCategory) => {
    setLoading(true);
    setError('');
    try {
      const params = category && category !== 'All' ? { category } : {};
      const res = await api.get('/forum/threads', { params });
      setThreads(res.data);
    } catch (err) {
      setError('Failed to load threads.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch posts for selected thread
  const fetchPosts = async (threadId) => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/forum/threads/${threadId}`);
      setPosts(res.data.posts);
      setSelectedThread(res.data.thread);
    } catch (err) {
      setError('Failed to load thread posts.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchThreads();
    // eslint-disable-next-line
  }, [selectedCategory]);

  // Handlers
  const handleCategory = (cat) => {
    setSelectedCategory(cat);
    setSelectedThread(null);
    setPosts([]);
    setError('');
    setSuccess('');
  };

  const handleCreateThread = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError('');
    setSuccess('');
    try {
      const res = await api.post('/forum/threads', {
        title: newThread.title,
        category: newThread.category,
      });
      // Post first post as thread content
      if (newThread.content) {
        await api.post(`/forum/threads/${res.data.id}/posts`, { content: newThread.content });
      }
      setNewThread({ title: '', content: '', category: CATEGORIES[0] });
      setSuccess('Thread created!');
      fetchThreads(newThread.category);
    } catch (err) {
      setError('Failed to create thread.');
    } finally {
      setCreating(false);
    }
  };

  const handleSelectThread = (thread) => {
    fetchPosts(thread.id);
    setError('');
    setSuccess('');
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setPosting(true);
    setError('');
    setSuccess('');
    try {
      await api.post(`/forum/threads/${selectedThread.id}/posts`, { content: reply });
      setReply('');
      fetchPosts(selectedThread.id);
      setSuccess('Reply posted!');
    } catch (err) {
      setError('Failed to post reply.');
    } finally {
      setPosting(false);
    }
  };

  const handleDeleteThread = async (threadId) => {
    if (!window.confirm('Delete this thread?')) return;
    setDeleting(true);
    setError('');
    setSuccess('');
    try {
      await api.delete(`/forum/threads/${threadId}`);
      setSelectedThread(null);
      setPosts([]);
      fetchThreads(selectedCategory);
      setSuccess('Thread deleted.');
    } catch (err) {
      setError('Failed to delete thread.');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    setDeleting(true);
    setError('');
    setSuccess('');
    try {
      const res = await api.delete(`/forum/posts/${postId}`);
      if (res.data.threadDeleted) {
        setSelectedThread(null);
        setPosts([]);
        setSuccess('Thread deleted.');
      } else {
        fetchPosts(selectedThread.id);
        setSuccess('Post deleted.');
      }
    } catch (err) {
      setError('Failed to delete post.');
    } finally {
      setDeleting(false);
    }
  };

  // Responsive: side-by-side on desktop, stacked on mobile
  return (
    <div className="w-full min-h-screen p-4 font-mono text-green-300 bg-black">
      {/* Responsive grid: 1/3 left, 2/3 right on desktop; stacked on mobile */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full max-w-7xl mx-auto">
        {/* Left column: image + thread list */}
        <div className="flex flex-col gap-4 md:col-span-1 w-full">
          {/* Image Card */}
          <div className="bg-[#0a0a0a] border border-green-500 rounded-xl shadow-lg w-full flex items-center justify-center overflow-hidden">
            <img src={forumImg} alt="Forum" className="w-full h-48 md:h-64 object-cover" onError={e => { e.target.style.display = 'none'; }} />
          </div>
          {/* Thread List (scrollable after 7 items) */}
          <div className="bg-[#111] border border-green-700 rounded p-3 w-full flex-1 max-h-[519px] overflow-y-auto">
            {error && <div className="text-red-400 font-bold mb-2">{error}</div>}
            {success && <div className="text-green-400 font-bold mb-2">{success}</div>}
            {loading ? <div>Loading threads...</div> : threads.length === 0 ? <div>No threads found.</div> : threads.map(thread => (
              <div
                key={thread.id}
                className={`bg-[#111] border border-green-700 rounded p-3 cursor-pointer hover:bg-green-900/20 transition-all ${selectedThread && selectedThread.id === thread.id ? 'border-2 border-green-400' : ''}`}
                onClick={() => handleSelectThread(thread)}
              >
                <div className="flex items-center justify-between">
                  <span className="font-bold text-green-300">{thread.title}</span>
                  <span className="text-xs text-yellow-300 ml-2">{thread.category}</span>
                </div>
                <div className="flex items-center gap-2 text-xs mt-1">
                  <span>by <span className="text-blue-300">{thread.username}</span></span>
                  <span className="opacity-70">|</span>
                  <span>{new Date(thread.updated_at).toLocaleString()}</span>
                  {user.id === thread.user_id && (
                    <button
                      className="px-3 py-1 rounded border border-red-600 bg-red-600 text-white transition-all hover:bg-transparent hover:text-red-500 hover:border-red-500 ml-auto text-xs"
                      onClick={e => { e.stopPropagation(); handleDeleteThread(thread.id); }}
                      disabled={deleting}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* Right column: category/create (top row), post viewer (bottom row) */}
        <div className="flex flex-col gap-4 md:col-span-2 w-full">
          {/* Top row: category selector + create thread */}
          <div className="flex flex-col md:flex-row gap-4 w-full">
            {/* Category Selector */}
            <div className="flex-1 bg-[#0a0a0a] border border-green-500 rounded-xl shadow-lg flex flex-col justify-between">
              <div className="bg-green-500 text-black font-extrabold text-2xl px-6 py-4 rounded-t-xl text-center uppercase tracking-wide mb-2">
                HANGAR BAY: 9 COMMS BOARD
              </div>
              <div className="px-6 pb-2 flex-1 flex flex-col justify-center">
                <button
                  className={`w-full border border-green-500 font-bold rounded py-2 mb-2 transition-all ${selectedCategory === 'All' ? 'bg-green-500 text-black' : 'bg-black text-green-400 hover:bg-green-900/30'}`}
                  onClick={() => handleCategory('All')}
                >
                  ALL
                </button>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  {CATEGORIES.map((cat, idx) => (
                    <button
                      key={cat}
                      className={`border border-green-500 font-bold rounded py-2 transition-all ${selectedCategory === cat ? 'bg-green-500 text-black' : 'bg-black text-green-400 hover:bg-green-900/30'}`}
                      onClick={() => handleCategory(cat)}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
                <button
                  className="w-full bg-green-500 text-black border border-green-500 font-bold rounded py-2 mt-1 hover:bg-green-400 transition-all"
                  onClick={() => fetchThreads(selectedCategory)}
                >
                  Reload
                </button>
              </div>
            </div>
            {/* Create Thread Card */}
            <form onSubmit={handleCreateThread} className="flex-1 bg-[#0a0a0a] border border-green-500 rounded-xl p-4 shadow-lg flex flex-col gap-2 justify-between">
              <div className="flex flex-col gap-1">
                <label className="text-green-400 font-bold">Category</label>
                <select
                  className="input-terminal"
                  value={newThread.category}
                  onChange={e => setNewThread(nt => ({ ...nt, category: e.target.value }))}
                >
                  {CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-green-400 font-bold">Title</label>
                <input
                  className="input-terminal"
                  value={newThread.title}
                  onChange={e => setNewThread(nt => ({ ...nt, title: e.target.value }))}
                  required
                  maxLength={100}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-green-400 font-bold">Content</label>
                <textarea
                  className="input-terminal"
                  value={newThread.content}
                  onChange={e => setNewThread(nt => ({ ...nt, content: e.target.value }))}
                  rows={3}
                  required
                  maxLength={1000}
                />
              </div>
              <button className="btn-primary mt-2" type="submit" disabled={creating}>Create Thread</button>
            </form>
          </div>
          {/* Post Viewer (bottom row, full width of right column) */}
          <div className="bg-[#0a0a0a] border border-green-500 rounded-xl p-4 shadow-lg w-full flex-1 min-h-[420px]">
            {selectedThread ? (
              <>
                <h3 className="text-xl font-bold text-green-400 mb-2">{selectedThread.title}</h3>
                <div className="text-xs text-yellow-300 mb-2">{selectedThread.category}</div>
                <div className="text-sm text-blue-300 mb-2">by {selectedThread.username} • {new Date(selectedThread.created_at).toLocaleString()}</div>
                {/* Replies List (scrollable) */}
                <div className="flex flex-col gap-3 mt-4 max-h-[360px] overflow-y-auto">
                  {posts.length === 0 ? <div>No posts yet.</div> : posts.map(post => (
                    <div key={post.id} className="bg-[#111] border border-green-700 rounded p-3 relative">
                      <div className="text-green-200 whitespace-pre-wrap">{post.content}</div>
                      <div className="flex items-center gap-2 text-xs mt-2">
                        <span>by <span className="text-blue-300">{post.username}</span></span>
                        <span className="opacity-70">|</span>
                        <span>{new Date(post.created_at).toLocaleString()}</span>
                        {user.id === post.user_id && (
                          <button
                            className="px-3 py-1 rounded border border-red-600 bg-red-600 text-white transition-all hover:bg-transparent hover:text-red-500 hover:border-red-500 ml-auto text-xs"
                            onClick={() => handleDeletePost(post.id)}
                            disabled={deleting}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {/* Reply Box */}
                <form onSubmit={handleReply} className="mt-4 flex flex-col gap-2">
                  <textarea
                    className="input-terminal"
                    value={reply}
                    onChange={e => setReply(e.target.value)}
                    rows={2}
                    placeholder="Write a reply..."
                    required
                    maxLength={1000}
                  />
                  <button className="btn-primary w-fit" type="submit" disabled={posting}>Reply</button>
                </form>
              </>
            ) : (
              <div className="flex items-center justify-center h-full min-h-[200px] text-green-400 text-lg font-bold opacity-70">
                Select a thread to view posts.
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Dimming overlay when deleting */}
      {deleting && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 transition-opacity pointer-events-none"></div>
      )}
    </div>
  );
};

export default Forum; 